// Builds the Sessions "metadata download" CSV. One row per uploaded video
// (session), with the GPS-derived district/state placed up front so the sheet
// is directly sortable by district. See districtLookup.ts for the mapping.
import { lookupDistrict } from './districtLookup';

// Loosely-typed view of the backend Session object returned by GET /sessions.
export interface MetadataSession {
  sessionId: string;
  projectId?: string;
  enumeratorName?: string;
  status?: string;
  recordingMode?: string;
  language?: string;
  dialect?: string;
  recordingEnvironment?: string;
  duration?: number;
  createdAt?: string;
  completedAt?: string;
  location?: {
    state?: string;
    district?: string;
    block?: string;
    village?: string;
    gps?: { lat?: number; lng?: number; accuracy?: number };
  };
  voiceOnly?: boolean;
  media?: {
    video?: { filename?: string; sizeBytes?: number; durationSeconds?: number; sha256?: string };
    audio?: { filename?: string; sizeBytes?: number; durationSeconds?: number; sha256?: string };
  };
  respondent?: Record<string, unknown>;
  // Answers to the project's custom metadata form, keyed by field id.
  metadataResponses?: Record<string, string>;
}

export interface EnrichedRow extends MetadataSession {
  mappedState: string;
  mappedDistrict: string;
  projectName?: string; // filled in by buildMetadataCsv from the project list
}

/** Attach GPS-derived state/district to each session (async — loads boundaries once). */
export async function enrichWithDistrict(sessions: MetadataSession[]): Promise<EnrichedRow[]> {
  const out: EnrichedRow[] = [];
  for (const s of sessions) {
    const gps = s.location?.gps;
    let hit = null;
    if (gps?.lat != null && gps?.lng != null) {
      hit = await lookupDistrict(gps.lat, gps.lng);
    }
    out.push({
      ...s,
      mappedState: hit?.state ?? '',
      mappedDistrict: hit?.district ?? '',
    });
  }
  return out;
}

type Column = { header: string; get: (r: EnrichedRow) => unknown };

const COLUMNS: Column[] = [
  { header: 'mapped_district', get: (r) => r.mappedDistrict },
  { header: 'mapped_state', get: (r) => r.mappedState },
  { header: 'session_id', get: (r) => r.sessionId },
  { header: 'project_id', get: (r) => r.projectId },
  { header: 'project_name', get: (r) => r.projectName },
  { header: 'enumerator', get: (r) => r.enumeratorName },
  { header: 'status', get: (r) => r.status },
  { header: 'source', get: (r) => (r.recordingMode === 'app' ? 'App' : r.recordingMode === 'gallery' ? 'Uploaded' : '') },
  { header: 'media_type', get: (r) => (r.voiceOnly || (!r.media?.video?.filename && r.media?.audio?.filename) ? 'Audio' : 'Video') },
  { header: 'language', get: (r) => r.language },
  { header: 'dialect', get: (r) => r.dialect },
  { header: 'recording_environment', get: (r) => r.recordingEnvironment },
  { header: 'duration_seconds', get: (r) => r.duration },
  { header: 'created_at', get: (r) => r.createdAt },
  { header: 'completed_at', get: (r) => r.completedAt },
  { header: 'gps_lat', get: (r) => r.location?.gps?.lat },
  { header: 'gps_lng', get: (r) => r.location?.gps?.lng },
  { header: 'gps_accuracy_m', get: (r) => r.location?.gps?.accuracy },
  { header: 'entered_state', get: (r) => r.location?.state },
  { header: 'entered_district', get: (r) => r.location?.district },
  { header: 'block', get: (r) => r.location?.block },
  { header: 'village', get: (r) => r.location?.village },
  { header: 'video_filename', get: (r) => r.media?.video?.filename },
  { header: 'video_size_bytes', get: (r) => r.media?.video?.sizeBytes },
  { header: 'video_duration_seconds', get: (r) => r.media?.video?.durationSeconds },
  { header: 'video_sha256', get: (r) => r.media?.video?.sha256 },
  { header: 'audio_filename', get: (r) => r.media?.audio?.filename },
  { header: 'audio_size_bytes', get: (r) => r.media?.audio?.sizeBytes },
  { header: 'respondent_name', get: (r) => resp(r, 'name') },
  { header: 'respondent_phone', get: (r) => resp(r, 'phone') },
  { header: 'respondent_age', get: (r) => resp(r, 'age') },
  { header: 'respondent_gender', get: (r) => resp(r, 'gender') },
  { header: 'respondent_language', get: (r) => resp(r, 'primaryLanguage') ?? resp(r, 'languagesSpoken') },
  { header: 'respondent_education', get: (r) => resp(r, 'educationLevel') },
  { header: 'respondent_occupation', get: (r) => resp(r, 'occupation') },
  { header: 'respondent_income_bracket', get: (r) => resp(r, 'monthlyIncomeBracket') },
  { header: 'respondent_socioeconomic_class', get: (r) => resp(r, 'socioeconomicClass') },
  { header: 'respondent_urban_tier', get: (r) => resp(r, 'urbanTier') },
  { header: 'respondent_family_size', get: (r) => resp(r, 'familySize') },
  { header: 'respondent_life_satisfaction', get: (r) => resp(r, 'lifeSatisfactionScore') },
  { header: 'respondent_consent', get: (r) => resp(r, 'consentGiven') },
  { header: 'respondent_notes', get: (r) => resp(r, 'notes') },
];

function resp(r: EnrichedRow, key: string): unknown {
  return r.respondent?.[key];
}

function escapeCell(v: unknown): string {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Build a CSV string (with header row) for the given enriched rows.
 *
 * `fieldLabelById` maps custom metadata-form field ids → human labels (built
 * from the projects' metadataForm definitions). Any field id present in a row's
 * `metadataResponses` gets its own `form: <label>` column, so survey answers are
 * sortable in the sheet.
 */
export function buildMetadataCsv(
  rows: EnrichedRow[],
  projectNameById: Record<string, string> = {},
  fieldLabelById: Record<string, string> = {},
): string {
  const withNames = rows.map((r) => ({
    ...r,
    projectName: r.projectId ? projectNameById[r.projectId] ?? '' : '',
  }));

  // Collect distinct custom-form field ids across all rows (first-seen order).
  const formFieldIds: string[] = [];
  const seen = new Set<string>();
  for (const r of withNames) {
    for (const id of Object.keys(r.metadataResponses ?? {})) {
      if (!seen.has(id)) {
        seen.add(id);
        formFieldIds.push(id);
      }
    }
  }
  const formColumns: Column[] = formFieldIds.map((id) => ({
    header: `form: ${fieldLabelById[id] ?? id}`,
    get: (r) => r.metadataResponses?.[id],
  }));
  const allColumns = [...COLUMNS, ...formColumns];

  const lines = [
    allColumns.map((c) => c.header).join(','),
    ...withNames.map((r) => allColumns.map((c) => escapeCell(c.get(r as EnrichedRow))).join(',')),
  ];
  return lines.join('\r\n');
}

/** Trigger a browser download of the CSV (UTF-8 BOM so Excel renders Hindi/₹). */
export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
